import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Lock, CalendarRange } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import SpaceFormModal from "../../components/scheduling/SpaceFormModal";
import BusinessHoursModal from "../../components/scheduling/BusinessHoursModal";
import Toast from "../../components/scheduling/Toast";
import { SkeletonRow } from "../../components/scheduling/Skeleton";

export default function Spaces() {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [spaces, setSpaces] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editSpace, setEditSpace] = useState(null);

  const [bhOpen, setBhOpen] = useState(false);
  const [bhSpace, setBhSpace] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const canAdminHint = true; // UI can show button; server enforces admin on save

  const activeCount = useMemo(
    () => spaces.filter((s) => s.status === "active").length,
    [spaces]
  );

  async function load() {
    if (!companyId) return;
    setBusy(true);
    setErr("");
    try {
      const { data, error } = await supabase
        .from("spaces")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSpaces(data || []);
    } catch (e) {
      setErr(e?.message || "Failed to load spaces");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // realtime refresh
    if (!companyId) return;
    const channel = supabase
      .channel("sched_spaces")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "spaces",
          filter: `company_id=eq.${companyId}`,
        },
        () => load()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div>
          <h3 className="sched-h3">Spaces</h3>
          <p className="sched-muted">
            {activeCount} active / {spaces.length} total
          </p>
        </div>
        {canAdminHint && (
          <button
            className="sched-btn primary"
            onClick={() => {
              setEditSpace(null);
              setModalOpen(true);
            }}
          >
            <Plus size={16} /> New space
          </button>
        )}
      </div>

      {busy ? (
        <>
          <SkeletonRow cols={4} />
          <SkeletonRow cols={4} />
          <SkeletonRow cols={4} />
        </>
      ) : err ? (
        <div className="sched-error">{err}</div>
      ) : spaces.length === 0 ? (
        <div className="sched-empty">
          <div className="sched-empty-title">No spaces yet</div>
          <div className="sched-muted">
            Create a space to start booking rooms/offices.
          </div>
        </div>
      ) : (
        <div className="sched-grid-cards">
          {spaces.map((s) => (
            <div
              key={s.id}
              className={`sched-space ${
                s.status !== "active" ? "inactive" : ""
              }`}
            >
              <div className="sched-space-top">
                <div>
                  <div className="sched-space-name">{s.name}</div>
                  <div className="sched-space-sub">
                    {s.location_building || "—"} • {s.location_floor || "—"} •{" "}
                    {s.location_section || "—"}
                  </div>
                </div>
                <div
                  className={`sched-pill ${
                    s.status === "active" ? "ok" : "off"
                  }`}
                >
                  {s.status === "active" ? "Active" : "Inactive"}
                </div>
              </div>

              <div className="sched-space-meta">
                <div>
                  <b>Capacity:</b> {s.capacity}
                </div>
                <div>
                  <b>Default:</b> {s.default_duration_min}m
                </div>
                <div>
                  <b>Min/Max:</b> {s.min_duration_min}m / {s.max_duration_min}m
                </div>
                <div>
                  <b>Buffer:</b> {s.buffer_before_min}m / {s.buffer_after_min}m
                </div>
              </div>

              {Array.isArray(s.tags) && s.tags.length ? (
                <div className="sched-tags">
                  {s.tags.slice(0, 6).map((t) => (
                    <span key={t} className="sched-tag">
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}

              {s.status !== "active" ? (
                <div className="sched-note">
                  <Lock size={16} />
                  <span>{s.inactive_reason || "Inactive"}</span>
                </div>
              ) : null}

              <div className="sched-space-actions">
                <button
                  className="sched-btn ghost"
                  onClick={() => {
                    setEditSpace(s);
                    setModalOpen(true);
                  }}
                >
                  <Pencil size={16} /> Edit
                </button>
                <button
                  className="sched-btn ghost"
                  onClick={() => {
                    setBhSpace(s);
                    setBhOpen(true);
                  }}
                >
                  Business hours
                </button>
                <button
                  className="sched-btn primary"
                  onClick={() => navigate(`/spaces/${s.id}`)}
                >
                  <CalendarRange size={16} /> View schedule
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SpaceFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        space={editSpace}
        onSaved={() => {
          setToast({ open: true, type: "success", message: "Saved" });
          load();
        }}
      />

      <BusinessHoursModal
        open={bhOpen}
        onClose={() => setBhOpen(false)}
        space={bhSpace}
      />

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}
